/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import {
  TablePlan,
  ModeleTable,
  ReservationPlan,
  FormeTable,
  ChangementPlan,
  StatutVisuelTable,
  InfoOccupationTable,
} from './types.ts';

export const TAILLE_GRILLE = 20; // 20px par unité de grille
export const SEUIL_AIMANTATION_BORD = 16; // 16px pour aimanter table contre table

/**
 * Arrondit une coordonnée à la grille la plus proche.
 */
export function aimanterSurGrille(valeur: number, pasGrille: number = TAILLE_GRILLE): number {
  return Math.round(valeur / pasGrille) * pasGrille;
}

/**
 * Boîte englobante AABB d'une table avec prise en compte simple des dimensions.
 */
export interface BoiteAABB {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function obtenirBoiteAABB(table: Pick<TablePlan, 'posX' | 'posY' | 'largeur' | 'hauteur'>): BoiteAABB {
  return {
    minX: table.posX,
    minY: table.posY,
    maxX: table.posX + table.largeur,
    maxY: table.posY + table.hauteur,
  };
}

/**
 * Vérifie si deux tables sont en collision (chevauchement strict).
 */
export function verifierCollisionTables(
  tableA: Pick<TablePlan, 'posX' | 'posY' | 'largeur' | 'hauteur'>,
  tableB: Pick<TablePlan, 'posX' | 'posY' | 'largeur' | 'hauteur'>,
  marge: number = 2
): boolean {
  const a = obtenirBoiteAABB(tableA);
  const b = obtenirBoiteAABB(tableB);

  return (
    a.minX + marge < b.maxX - marge &&
    a.maxX - marge > b.minX + marge &&
    a.minY + marge < b.maxY - marge &&
    a.maxY - marge > b.minY + marge
  );
}

/**
 * Calcule l'aimantation d'une table en mouvement par rapport à la grille ET aux bords des autres tables.
 */
export function calculerAimantation(
  nouvellePos: { x: number; y: number },
  largeur: number,
  hauteur: number,
  autresTables: TablePlan[],
  seuilBord: number = SEUIL_AIMANTATION_BORD,
  pasGrille: number = TAILLE_GRILLE
): { x: number; y: number; aimanteeAuBord: boolean; tableReferenceId?: string } {
  // 1. Aimantation de base sur la grille
  let resultatX = aimanterSurGrille(nouvellePos.x, pasGrille);
  let resultatY = aimanterSurGrille(nouvellePos.y, pasGrille);
  let aimanteeAuBord = false;
  let tableRef: string | undefined = undefined;

  let minDistanceBord = seuilBord;

  // 2. Recherche d'un bord proche sur une autre table
  for (const table of autresTables) {
    const tGauche = table.posX;
    const tDroite = table.posX + table.largeur;
    const tHaut = table.posY;
    const tBas = table.posY + table.hauteur;

    // Alignement horizontal potentiel (gauche contre droite ou droite contre gauche)
    const chevauchementY =
      Math.max(0, Math.min(nouvellePos.y + hauteur, tBas) - Math.max(nouvellePos.y, tHaut));

    if (chevauchementY > 10) {
      // Bord droit de notre table contre bord gauche de l'autre
      const distDroiteVersGauche = Math.abs(nouvellePos.x + largeur - tGauche);
      if (distDroiteVersGauche < minDistanceBord) {
        resultatX = tGauche - largeur;
        minDistanceBord = distDroiteVersGauche;
        aimanteeAuBord = true;
        tableRef = table.id;
      }

      // Bord gauche de notre table contre bord droit de l'autre
      const distGaucheVersDroite = Math.abs(nouvellePos.x - tDroite);
      if (distGaucheVersDroite < minDistanceBord) {
        resultatX = tDroite;
        minDistanceBord = distGaucheVersDroite;
        aimanteeAuBord = true;
        tableRef = table.id;
      }
    }

    // Alignement vertical potentiel (haut contre bas ou bas contre haut)
    const chevauchementX =
      Math.max(0, Math.min(nouvellePos.x + largeur, tDroite) - Math.max(nouvellePos.x, tGauche));

    if (chevauchementX > 10) {
      // Bord bas de notre table contre bord haut de l'autre
      const distBasVersHaut = Math.abs(nouvellePos.y + hauteur - tHaut);
      if (distBasVersHaut < minDistanceBord) {
        resultatY = tHaut - hauteur;
        minDistanceBord = distBasVersHaut;
        aimanteeAuBord = true;
        tableRef = table.id;
      }

      // Bord haut de notre table contre bord bas de l'autre
      const distHautVersBas = Math.abs(nouvellePos.y - tBas);
      if (distHautVersBas < minDistanceBord) {
        resultatY = tBas;
        minDistanceBord = distHautVersBas;
        aimanteeAuBord = true;
        tableRef = table.id;
      }
    }
  }

  return { x: resultatX, y: resultatY, aimanteeAuBord, tableReferenceId: tableRef };
}

const LIBELLE_FORME: Record<string, string> = { carree: 'carrée', ronde: 'ronde', rectangulaire: 'rectangulaire', ovale: 'ovale', plateau: 'plateau' };

/** Même texte que le serveur (MESSAGE_RONDES, functions/api/modules/reservations_plan.js) : la règle est la MÊME des deux côtés. */
export const MESSAGE_RONDES = 'Deux tables rondes ne se combinent pas par défaut. Pour l’autoriser, déclarez la combinaison dans les formats de tables (« peut se combiner avec »).';

/**
 * Règle de combinaison de tables, identique à celle du serveur (refusCombinaison). SOURCE DE VÉRITÉ : la bibliothèque de formats, c'est-à-dire
 * « peut se combiner avec » (la combinaison doit être acceptée par les DEUX modèles). Quand les deux tables n'ont pas de modèle connu :
 * même forme exigée, et deux tables rondes ne se combinent pas par défaut. Les messages sont ceux du serveur.
 */
export function verifierCompatibiliteCombinaison(
  tables: TablePlan[],
  modelesMap: Map<string, ModeleTable>
): { compatible: boolean; raison?: string } {
  if (tables.length < 2) {
    return { compatible: false, raison: 'Sélectionnez au moins 2 tables pour les fusionner.' };
  }

  // Les tables doivent appartenir à la même zone
  if (new Set(tables.map((t) => t.zoneId)).size > 1) {
    return { compatible: false, raison: 'Ces tables sont dans des zones différentes : on ne fusionne que des tables voisines d’une même zone.' };
  }

  const a = tables[0];
  for (const b of tables.slice(1)) {
    const ma = a.modeleId ? modelesMap.get(a.modeleId) : undefined;
    const mb = b.modeleId ? modelesMap.get(b.modeleId) : undefined;
    if (ma && mb) {
      if (ma.combinableAvec.includes(mb.id) && mb.combinableAvec.includes(ma.id)) continue;
      return { compatible: false, raison: `Le modèle « ${ma.nom || ma.nomCourt} » et le modèle « ${mb.nom || mb.nomCourt} » ne se combinent pas (la combinaison doit être acceptée par les deux modèles).` };
    }
    if (a.forme !== b.forme) {
      return { compatible: false, raison: `Une table ${LIBELLE_FORME[a.forme] || a.forme} ne se combine pas avec une table ${LIBELLE_FORME[b.forme] || b.forme} : fusionnez des tables du même type.` };
    }
    if (a.forme === 'ronde') return { compatible: false, raison: MESSAGE_RONDES };
  }
  return { compatible: true };
}

/**
 * Calcule la fusion de tables :
 * Attribue un groupeId commun, calcule la capacité combinée (somme des capacités ou plateau),
 * et regroupe visuellement.
 */
export function fusionnerTables(
  tablesOriginales: TablePlan[],
  tableIdsAFusionner: string[],
  nouvelleCapaciteOptionnelle?: number
): { tablesModifiees: TablePlan[]; groupeId: string; nouvelleCapacite: number } {
  // Identifiant STABLE (même fusion rejouée = même identifiant) : annuler/refaire et « séparer » dans le même brouillon retrouvent le groupe, et le serveur
  // reconnaît la même clé (cleLocaleDeGroupe dans reservations_plan.js).
  const groupeId = `grp_${[...tableIdsAFusionner].map(String).sort().join('+')}`;
  const cibles = tablesOriginales.filter((t) => tableIdsAFusionner.includes(t.id));

  const capaciteCalculee =
    nouvelleCapaciteOptionnelle ??
    cibles.reduce((acc, t) => acc + t.capaciteMax, 0);

  const tablesModifiees = tablesOriginales.map((table) => {
    if (tableIdsAFusionner.includes(table.id)) {
      return {
        ...table,
        groupeId,
        // On conserve la trace mais le groupe possède la capacité collective
      };
    }
    return table;
  });

  return { tablesModifiees, groupeId, nouvelleCapacite: capaciteCalculee };
}

/**
 * Sépare un groupe de tables fusionnées :
 * Supprime le groupeId et restaure leur statut individuel.
 */
export function separerGroupe(tablesOriginales: TablePlan[], groupeId: string): TablePlan[] {
  return tablesOriginales.map((table) => {
    if (table.groupeId === groupeId) {
      const { groupeId: _, ...reste } = table;
      return reste;
    }
    return table;
  });
}

/**
 * Applique UN changement du brouillon à une liste de tables (pur). Sert à rejouer le brouillon : annuler, refaire.
 */
export function appliquerChangement(tables: TablePlan[], ch: ChangementPlan): TablePlan[] {
  switch (ch.type) {
    case 'deplacer': return tables.map((t) => (t.id === ch.tableId ? { ...t, posX: ch.posX, posY: ch.posY } : t));
    case 'pivoter': return tables.map((t) => (t.id === ch.tableId ? { ...t, rotation: ch.rotation } : t));
    case 'capacite': return tables.map((t) => (t.id === ch.tableId ? { ...t, capaciteMax: ch.capaciteMax } : t));
    case 'forme': return tables.map((t) => (t.id === ch.tableId ? { ...t, forme: ch.forme } : t));
    case 'fusionner': return fusionnerTables(tables, ch.tableIds, ch.nouvelleCapacite).tablesModifiees;
    case 'separer': return separerGroupe(tables, ch.groupeId);
    case 'bloquer': return tables.map((t) => (t.id === ch.tableId ? { ...t, bloquee: ch.bloquee, motifBlocage: ch.bloquee ? ch.motif : undefined } : t));
    case 'renommer': return tables.map((t) => (t.id === ch.tableId ? { ...t, nom: ch.nom } : t));
    case 'supprimer': return tables.filter((t) => t.id !== ch.tableId);
    case 'creer': { const id = (ch.table as { id?: string }).id; return id && tables.some((t) => t.id === id) ? tables : [...tables, { ...ch.table, id: id || `cree_${tables.length}` } as TablePlan]; }
    case 'dupliquer': return tables;
  }
}

/** Rejoue tout le brouillon sur les tables d'origine. */
export function rejouerChangements(tablesOrigine: TablePlan[], changements: ChangementPlan[]): TablePlan[] {
  return changements.reduce(appliquerChangement, tablesOrigine);
}

/**
 * Analyse l'heure (format "HH:mm" ou ISO) en minutes depuis minuit.
 */
/** « HH:MM » d'une heure ou d'une date locale (« 2026-10-13T12:00:00 »). Le contrat (types.ts) fournit des dates complètes. */
export function heureDe(valeur: string): string {
  return (valeur.includes('T') ? valeur.split('T')[1] : valeur).substring(0, 5);
}

export function convertirHeureEnMinutes(heureStr: string): number {
  if (heureStr.includes('T')) {
    const timePart = heureStr.split('T')[1]?.substring(0, 5) || '12:00';
    const [h, m] = timePart.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  }
  const [h, m] = heureStr.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/**
 * Vérifie si deux plages horaires se chevauchent.
 */
export function chevauchementCreneau(
  debutA: string,
  finA: string,
  debutB: string,
  finB: string
): boolean {
  const startA = convertirHeureEnMinutes(debutA);
  const endA = convertirHeureEnMinutes(finA);
  const startB = convertirHeureEnMinutes(debutB);
  const endB = convertirHeureEnMinutes(finB);

  return startA < endB && endA > startB;
}

/**
 * Détermine le statut visuel et les informations d'occupation d'une table à une heure donnée.
 */
export function evaluerStatutTable(
  table: TablePlan,
  reservations: ReservationPlan[],
  heureAffichee: string,
  maintenantReel: string = heureAffichee
): InfoOccupationTable {
  if (table.bloquee) {
    return {
      statut: 'bloquee',
      reservationsDuCreneau: [],
      estEnRetard: false,
    };
  }

  const minutesAffichee = convertirHeureEnMinutes(heureAffichee);
  const minutesReelles = convertirHeureEnMinutes(maintenantReel);

  // Réservations associées à cette table ou à son groupe
  const tableReservations = reservations.filter((r) =>
    r.tableIds.includes(table.id) && r.statut !== 'annulee' && r.statut !== 'no_show'
  );

  // Réservations actives sur le créneau de l'heure affichée
  // Pour les clients arrivés / walk-in non terminés : s'ils sont arrivés, ils occupent la table
  // même si l'heure affichée a dépassé l'heure de fin prévue !
  const reservationsActives = tableReservations.filter((r) => {
    const start = convertirHeureEnMinutes(r.debut);
    const end = convertirHeureEnMinutes(r.fin);
    
    if (r.statut === 'arrivee' || r.statut === 'walk_in') {
      // Arrivé et toujours pas terminé : occupe la table depuis le début jusqu'à 3h après
      return minutesAffichee >= start && minutesAffichee <= end + 180;
    }
    return minutesAffichee >= start && minutesAffichee < end;
  });

  // Conflit si plus d'une réservation active sur la même table
  if (reservationsActives.length > 1) {
    return {
      statut: 'conflit',
      reservationsDuCreneau: reservationsActives,
      estEnRetard: false,
    };
  }

  const courante = reservationsActives[0];

  if (courante) {
    const finMinutes = convertirHeureEnMinutes(courante.fin);
    const debutMinutes = convertirHeureEnMinutes(courante.debut);
    const tempsEcoule = Math.max(0, minutesReelles - debutMinutes);
    const tempsRestant = finMinutes - minutesReelles;

    // Détection "À libérer" : statut 'arrivee' ou 'walk_in' et durée dépassée
    const estDepassee =
      (courante.statut === 'arrivee' || courante.statut === 'walk_in') &&
      minutesAffichee >= finMinutes;

    if (estDepassee) {
      return {
        statut: 'a_liberer',
        reservationEnCours: courante,
        reservationsDuCreneau: reservationsActives,
        estEnRetard: true,
        tempsRestantMinutes: tempsRestant,
        minutesEcoulees: tempsEcoule,
      };
    }

    if (courante.statut === 'terminee') {
      return {
        statut: 'a_debarrasser',
        reservationEnCours: courante,
        reservationsDuCreneau: reservationsActives,
        estEnRetard: false,
        minutesEcoulees: tempsEcoule,
      };
    }

    if (courante.statut === 'arrivee' || courante.statut === 'walk_in') {
      return {
        statut: 'en_cours',
        reservationEnCours: courante,
        reservationsDuCreneau: reservationsActives,
        estEnRetard: false,
        tempsRestantMinutes: tempsRestant,
        minutesEcoulees: tempsEcoule,
      };
    }

    // Réservation future confirmée ou à confirmer
    return {
      statut: 'reservee',
      reservationEnCours: courante,
      reservationsDuCreneau: reservationsActives,
      estEnRetard: false,
      tempsRestantMinutes: debutMinutes - minutesAffichee,
    };
  }

  // Vérifier si une réservation arrive bientôt (< 45 min)
  const futureProche = tableReservations
    .filter((r) => {
      const start = convertirHeureEnMinutes(r.debut);
      return start > minutesAffichee && start - minutesAffichee <= 45;
    })
    .sort((a, b) => convertirHeureEnMinutes(a.debut) - convertirHeureEnMinutes(b.debut))[0];

  if (futureProche) {
    return {
      statut: 'reservee',
      prochaineReservation: futureProche,
      reservationsDuCreneau: [],
      estEnRetard: false,
      tempsRestantMinutes: convertirHeureEnMinutes(futureProche.debut) - minutesAffichee,
    };
  }

  return {
    statut: 'libre',
    reservationsDuCreneau: [],
    estEnRetard: false,
  };
}

/**
 * Calcul de capacité par taille de groupe en direct.
 * Retourne :
 * - couvertsTotaux : capacité maximale de toutes les tables actives
 * - couvertsDisponibles : capacité des tables actuellement non occupées
 * - parTailleGroupe : { '2': 3, '4': 2, '6': 1, '8': 1, '12+': 0 }
 * Respecte les zones 'compteAPart' (exclues si filtre zone actif).
 */
export function calculerCapacitesEnDirect(
  tables: TablePlan[],
  reservations: ReservationPlan[],
  heureAffichee: string,
  zoneIdFiltre?: string
): {
  couvertsTotaux: number;
  couvertsDisponibles: number;
  tablesDisponiblesParTaille: Record<string, number>;
  resumeTexte: string;
} {
  // Regroupement des tables fusionnées (compter une table fusionnée comme une seule unité de capacité)
  const groupesTraites = new Set<string>();
  const tablesFiltrees = tables.filter((t) => {
    if (zoneIdFiltre && t.zoneId !== zoneIdFiltre) return false;
    return true;
  });

  let couvertsTotaux = 0;
  let couvertsDisponibles = 0;
  const compteurs: Record<string, number> = {
    '2': 0,
    '4': 0,
    '6': 0,
    '8': 0,
    '12+': 0,
  };

  for (const table of tablesFiltrees) {
    if (table.groupeId) {
      if (groupesTraites.has(table.groupeId)) continue;
      groupesTraites.add(table.groupeId);

      const tablesDuGroupe = tablesFiltrees.filter((t) => t.groupeId === table.groupeId);
      const capTotale = tablesDuGroupe.reduce((sum, t) => sum + t.capaciteMax, 0);
      couvertsTotaux += capTotale;

      // Évaluer si libre
      const toutLibre = tablesDuGroupe.every((t) => {
        const evalStatut = evaluerStatutTable(t, reservations, heureAffichee);
        return evalStatut.statut === 'libre';
      });

      if (toutLibre && !tablesDuGroupe.some((t) => t.bloquee)) {
        couvertsDisponibles += capTotale;
        classerTailleGroupe(capTotale, compteurs);
      }
    } else {
      couvertsTotaux += table.capaciteMax;
      const evalStatut = evaluerStatutTable(table, reservations, heureAffichee);

      if (evalStatut.statut === 'libre' && !table.bloquee) {
        couvertsDisponibles += table.capaciteMax;
        classerTailleGroupe(table.capaciteMax, compteurs);
      }
    }
  }

  const morceaux: string[] = [];
  if (compteurs['2'] > 0) morceaux.push(`${compteurs['2']} table${compteurs['2'] > 1 ? 's' : ''} de 2`);
  if (compteurs['4'] > 0) morceaux.push(`${compteurs['4']} table${compteurs['4'] > 1 ? 's' : ''} de 4`);
  if (compteurs['6'] > 0) morceaux.push(`${compteurs['6']} table${compteurs['6'] > 1 ? 's' : ''} de 6`);
  if (compteurs['8'] > 0) morceaux.push(`${compteurs['8']} table${compteurs['8'] > 1 ? 's' : ''} de 8`);
  if (compteurs['12+'] > 0) morceaux.push(`${compteurs['12+']} grande${compteurs['12+'] > 1 ? 's' : ''} table(s)`);

  const resumeTexte = morceaux.length > 0 ? morceaux.join(', ') : 'Aucune table disponible';

  return {
    couvertsTotaux,
    couvertsDisponibles,
    tablesDisponiblesParTaille: compteurs,
    resumeTexte,
  };
}

function classerTailleGroupe(capacite: number, compteurs: Record<string, number>): void {
  if (capacite <= 2) compteurs['2'] = (compteurs['2'] || 0) + 1;
  else if (capacite <= 4) compteurs['4'] = (compteurs['4'] || 0) + 1;
  else if (capacite <= 6) compteurs['6'] = (compteurs['6'] || 0) + 1;
  else if (capacite <= 8) compteurs['8'] = (compteurs['8'] || 0) + 1;
  else compteurs['12+'] = (compteurs['12+'] || 0) + 1;
}

/**
 * Propose une attribution automatique optimale pour une réservation donnée.
 * Règle d'or : minimise le gaspillage de couverts sans conflit de créneau horaire.
 */
export function proposerAttributionAuto(
  reservation: ReservationPlan,
  tables: TablePlan[],
  toutesReservations: ReservationPlan[]
): {
  recommandee: boolean;
  tableIds: string[];
  nomTable: string;
  raison: string;
} {
  const couverts = reservation.couverts;
  const debut = reservation.debut;
  const fin = reservation.fin;

  // Filtrer les tables libres sur le créneau exact
  const tablesCandidates = tables.filter((table) => {
    if (table.bloquee) return false;
    // Vérifier les chevauchements
    const aConflit = toutesReservations.some(
      (r) =>
        r.id !== reservation.id &&
        r.tableIds.includes(table.id) &&
        r.statut !== 'annulee' &&
        r.statut !== 'no_show' &&
        chevauchementCreneau(r.debut, r.fin, debut, fin)
    );
    return !aConflit;
  });

  // 1. Chercher une table simple dont la capacité max est >= couverts
  const tablesSimples = tablesCandidates
    .filter((t) => t.capaciteMax >= couverts)
    .sort((a, b) => {
      // Priorité 1 : gaspillage de place minimal
      const diffA = a.capaciteMax - couverts;
      const diffB = b.capaciteMax - couverts;
      if (diffA !== diffB) return diffA - diffB;
      // Priorité 2 : adéquation de forme ou nom
      return a.nom.localeCompare(b.nom);
    });

  if (tablesSimples.length > 0) {
    const meilleure = tablesSimples[0];
    const surcapacite = meilleure.capaciteMax - couverts;
    return {
      recommandee: true,
      tableIds: [meilleure.id],
      nomTable: meilleure.nom,
      raison: `Table ${meilleure.nom} (${meilleure.capaciteMax} pl.) adaptée pour ${couverts} pers.${
        surcapacite > 0 ? ` (+${surcapacite} libre)` : ' (ajustement parfait)'
      }`,
    };
  }

  // 2. Si aucune table simple ne suffit, chercher une combinaison de 2 tables adjacentes compatibles
  for (let i = 0; i < tablesCandidates.length; i++) {
    for (let j = i + 1; j < tablesCandidates.length; j++) {
      const t1 = tablesCandidates[i];
      const t2 = tablesCandidates[j];
      if (t1.zoneId === t2.zoneId && t1.capaciteMax + t2.capaciteMax >= couverts) {
        // Vérifier compatibilité de formes
        if (t1.forme === 'ronde' || t2.forme === 'ronde') continue;
        return {
          recommandee: true,
          tableIds: [t1.id, t2.id],
          nomTable: `${t1.nom} + ${t2.nom}`,
          raison: `Combinaison ${t1.nom} + ${t2.nom} (${t1.capaciteMax + t2.capaciteMax} pl.)`,
        };
      }
    }
  }

  return {
    recommandee: false,
    tableIds: [],
    nomTable: '',
    raison: 'Aucune table disponible avec une capacité suffisante sur ce créneau.',
  };
}

/**
 * Calcule l'impact d'une liste de changements sur les réservations existantes.
 * Détecte les réductions de capacité, suppressions ou fusions qui laissent des réservations sans place.
 * Règle impérative : une réservation déjà assise ('arrivee' ou 'walk_in') est signalée avec vigilance.
 */
export function calculerImpactChangements(
  tablesInitiales: TablePlan[],
  changements: ChangementPlan[],
  reservations: ReservationPlan[]
): {
  reservationsTouchees: {
    reservationId: string;
    raison: string;
    propositions: ('replacer_auto' | 'garder' | 'contacter')[];
    estAssise: boolean;
  }[];
  avertissements: string[];
} {
  const reservationsTouchees: {
    reservationId: string;
    raison: string;
    propositions: ('replacer_auto' | 'garder' | 'contacter')[];
    estAssise: boolean;
  }[] = [];
  const avertissements: string[] = [];

  const mapTables = new Map<string, TablePlan>();
  tablesInitiales.forEach((t) => mapTables.set(t.id, { ...t }));

  for (const changement of changements) {
    if (changement.type === 'capacite') {
      const table = mapTables.get(changement.tableId);
      if (table && changement.capaciteMax < table.capaciteMax) {
        // Chercher les réservations dont les couverts dépassent la nouvelle capacité
        const affectees = reservations.filter(
          (r) =>
            r.tableIds.includes(changement.tableId) &&
            r.couverts > changement.capaciteMax &&
            r.statut !== 'annulee'
        );
        for (const aff of affectees) {
          const estAssise = aff.statut === 'arrivee' || aff.statut === 'walk_in';
          if (estAssise) {
            avertissements.push(
              `Attention : ${aff.nom} (${aff.couverts} couverts) est déjà installé(e) à table ${table.nom}. Confirmation requise avant tout déplacement.`
            );
          }
          reservationsTouchees.push({
            reservationId: aff.id,
            raison: `Table ${table.nom} réduite à ${changement.capaciteMax} places (réservation de ${aff.couverts} personnes)`,
            propositions: ['replacer_auto', 'garder', 'contacter'],
            estAssise,
          });
        }
      }
    } else if (changement.type === 'supprimer') {
      const table = mapTables.get(changement.tableId);
      if (table) {
        const affectees = reservations.filter(
          (r) => r.tableIds.includes(table.id) && r.statut !== 'annulee'
        );
        for (const aff of affectees) {
          const estAssise = aff.statut === 'arrivee' || aff.statut === 'walk_in';
          if (estAssise) {
            avertissements.push(
              `Sécurité : Table ${table.nom} a des clients installés (${aff.nom}). Impossible de supprimer sans recaser d'abord les clients.`
            );
          }
          reservationsTouchees.push({
            reservationId: aff.id,
            raison: `Suppression de la table ${table.nom}`,
            propositions: ['replacer_auto', 'garder', 'contacter'],
            estAssise,
          });
        }
      }
    } else if (changement.type === 'bloquer' && changement.bloquee) {
      const table = mapTables.get(changement.tableId);
      if (table) {
        const affectees = reservations.filter(
          (r) => r.tableIds.includes(table.id) && r.statut !== 'annulee'
        );
        for (const aff of affectees) {
          const estAssise = aff.statut === 'arrivee' || aff.statut === 'walk_in';
          reservationsTouchees.push({
            reservationId: aff.id,
            raison: `Blocage de la table ${table.nom} (${changement.motif || 'maintenance'})`,
            propositions: ['replacer_auto', 'garder', 'contacter'],
            estAssise,
          });
        }
      }
    }
  }

  return { reservationsTouchees, avertissements };
}
